import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { useFieldAuth } from '../../contexts/FieldContext';
import { useRefereeAuth } from '../../contexts/RefereeContext';
import { useCart } from '../../contexts/CartContext';
import { CLAN_TYPES } from '../../types/clan';
import ThemeToggle from '../common/ThemeToggle';

const withAlpha = (color: string, alpha: number) => {
  if (color.startsWith('#')) {
    const hex = color.replace('#', '');
    const normalized = hex.length === 3
      ? hex.split('').map(ch => `${ch}${ch}`).join('')
      : hex;
    const bigint = parseInt(normalized, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  if (color.startsWith('rgb')) {
    const values = color
      .replace('rgba', '')
      .replace('rgb', '')
      .replace('(', '')
      .replace(')', '')
      .split(',')
      .map(v => v.trim());

    const [r = '0', g = '0', b = '0'] = values;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  return color;
};

const getHeaderBackground = (theme: any) => theme.styles?.headerBackground || withAlpha(theme.colors.background, 0.9);
const getHeaderBorder = (theme: any) => theme.styles?.headerBorder || theme.colors.borderMuted;
const getMutedText = (theme: any) => theme.colors.textMuted || theme.colors.textSecondary;

const Header = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: ${({ theme }) => getHeaderBackground(theme)};
  border-bottom: 1px solid ${({ theme }) => getHeaderBorder(theme)};
  padding: 16px 0;
  z-index: 1000;
  backdrop-filter: blur(10px);
  
  @media (max-width: 768px) {
    padding: 16px 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 24px;
  
  @media (max-width: 768px) {
    padding: 0 16px;
  }
`;

const Logo = styled(Link)`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 20px;
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  font-weight: 600;
  letter-spacing: -0.5px;
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const DesktopNav = styled.nav`
  display: flex;
  gap: 8px;
  align-items: center;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  flex-direction: column;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  
  @media (max-width: 768px) {
    display: flex;
  }
`;

const BurgerLine = styled.div<{ $isOpen: boolean; $index: number }>`
  width: 18px;
  height: 2px;
  background: ${({ theme }) => getMutedText(theme)};
  margin: 2px 0;
  transition: all 0.2s ease;
  border-radius: 1px;
  transform-origin: center;
  
  ${props => props.$isOpen && props.$index === 0 && `
    transform: rotate(45deg) translate(6px, 6px);
  `}
  
  ${props => props.$isOpen && props.$index === 1 && `
    opacity: 0;
  `}
  
  ${props => props.$isOpen && props.$index === 2 && `
    transform: rotate(-45deg) translate(6px, -6px);
  `}
`;

const MobileMenuOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: ${({ theme }) => withAlpha(theme.colors.background, 0.85)};
  z-index: 999;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
  
  @media (min-width: 769px) {
    display: none;
  }
`;

const MobileMenu = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: 280px;
  height: 100vh;
  background: ${({ theme }) => getHeaderBackground(theme)};
  backdrop-filter: blur(20px);
  border-left: 1px solid ${({ theme }) => getHeaderBorder(theme)};
  padding: 80px 20px 20px;
  transform: translateX(${props => props.$isOpen ? '0' : '100%'});
  transition: transform 0.3s ease;
  z-index: 1000;
  
  @media (min-width: 769px) {
    display: none;
  }
`;

const MobileNavList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 40px;
`;

const MobileNavLink = styled(Link)<{ $active?: boolean }>`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 18px;
  color: ${({ $active, theme }) => ($active ? theme.colors.primary : getMutedText(theme))};
  text-decoration: none;
  padding: 10px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderMuted};
  transition: all 0.2s ease;
  
  &:hover {
    color: ${({ theme }) => theme.colors.text};
    padding-left: 10px;
  }
`;

const MobileClansSection = styled.div`
  margin-bottom: 20px;
`;

const MobileClansSectionTitle = styled.div`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 14px;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 10px;
  padding-bottom: 5px;
  border-bottom: 1px solid ${({ theme }) => withAlpha(theme.colors.primary, 0.3)};
  font-weight: 600;
`;

const MobileClansLink = styled(Link)`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 16px;
  color: ${({ theme }) => getMutedText(theme)};
  text-decoration: none;
  display: block;
  padding: 8px 0;
  margin-left: 15px;
  transition: all 0.2s ease;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    padding-left: 10px;
  }
`;

const MobileCartSection = styled.div`
  padding: 20px 0;
  border-top: 1px solid ${({ theme }) => theme.colors.borderMuted};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderMuted};
  margin-bottom: 20px;
`;

const MobileThemeToggleWrapper = styled.div`
  padding: 10px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderMuted};
`;

const MobileCartLinkWrapper = styled.div`
  margin-top: 10px;
`;

const MobileCartLink = styled(Link)`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 16px;
  color: ${({ theme }) => getMutedText(theme)};
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 0;
  
  &:hover {
    color: ${({ theme }) => theme.colors.text};
  }
`;

const MobileAuthSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const MobileAuthButton = styled.button`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 16px;
  color: ${({ theme }) => getMutedText(theme)};
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.borderMuted};
  border-radius: 6px;
  padding: 10px 0;
  text-align: center;
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:hover {
    background: ${({ theme }) => withAlpha(theme.colors.primary, 0.08)};
    color: ${({ theme }) => theme.colors.text};
    border-color: ${({ theme }) => theme.colors.border};
  }
  
  &.primary {
    background: ${({ theme }) => theme.colors.primary};
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.background};
  }
  
  &.primary:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
    border-color: ${({ theme }) => theme.colors.primaryDark};
  }
`;

const MobileLogoutButton = styled.button`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 16px;
  color: ${({ theme }) => getMutedText(theme)};
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.borderMuted};
  border-radius: 6px;
  cursor: pointer;
  padding: 10px 0;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${({ theme }) => withAlpha(theme.colors.primary, 0.08)};
    color: ${({ theme }) => theme.colors.text};
    border-color: ${({ theme }) => theme.colors.border};
  }
`;

const NavLink = styled(Link)<{ $active?: boolean }>`
  font-family: ${({ theme }) => theme.fonts.heading};
  color: ${({ $active, theme }) => ($active ? theme.colors.primary : getMutedText(theme))};
  text-decoration: none;
  padding: 8px 16px;
  border-radius: 6px;
  transition: all 0.2s ease;
  font-weight: 500;
  font-size: 16px;
  position: relative;

  &:hover {
    color: ${({ theme }) => theme.colors.text};
    background: ${({ theme }) => withAlpha(theme.colors.primary, 0.08)};
  }

  ${props => props.$active && `
    color: ${props.theme.colors.primary};
    background: ${withAlpha(props.theme.colors.primary, 0.12)};
  `}
`;

const ClansDropdown = styled.div`
  position: relative;
`;

const ClansDropdownButton = styled.button<{ $active?: boolean }>`
  font-family: ${({ theme }) => theme.fonts.heading};
  color: ${({ $active, theme }) => ($active ? theme.colors.primary : getMutedText(theme))};
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px 16px;
  border-radius: 6px;
  transition: all 0.2s ease;
  font-weight: 500;
  font-size: 16px;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    color: ${({ theme }) => theme.colors.text};
    background: ${({ theme }) => withAlpha(theme.colors.primary, 0.08)};
  }

  ${props => props.$active && `
    color: ${props.theme.colors.primary};
    background: ${withAlpha(props.theme.colors.primary, 0.12)};
  `}
`;

const ClansDropdownContent = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  background: ${({ theme }) => getHeaderBackground(theme)};
  backdrop-filter: blur(20px);
  border: 1px solid ${({ theme }) => getHeaderBorder(theme)};
  border-radius: 8px;
  min-width: 180px;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transform: translateY(${props => props.$isOpen ? '0' : '-8px'});
  transition: all 0.2s ease;
  box-shadow: 0 8px 32px ${({ theme }) => withAlpha(theme.colors.background, 0.8)};
  padding: 4px;
`;

const ClansDropdownItem = styled(Link)`
  display: block;
  padding: 10px 12px;
  color: ${({ theme }) => getMutedText(theme)};
  text-decoration: none;
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 13px;
  transition: all 0.2s ease;
  border-radius: 4px;

  &:hover {
    background: ${({ theme }) => withAlpha(theme.colors.primary, 0.12)};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const DesktopRightSection = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const CartLink = styled(Link)`
  font-family: ${({ theme }) => theme.fonts.heading};
  color: ${({ theme }) => getMutedText(theme)};
  text-decoration: none;
  padding: 8px 12px;
  border-radius: 6px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 500;

  &:hover {
    color: ${({ theme }) => theme.colors.text};
    background: ${({ theme }) => withAlpha(theme.colors.primary, 0.08)};
  }
`;

const CartCount = styled.span`
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.background};
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  min-width: 18px;
  text-align: center;
`;

const AuthLinks = styled.div`
  display: flex;
  gap: 12px;
`;

const AuthButton = styled.button<{ $active?: boolean }>`
  font-family: ${({ theme }) => theme.fonts.heading};
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.borderMuted};
  border-radius: 6px;
  color: ${({ theme }) => getMutedText(theme)};
  padding: 8px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;
  font-size: 14px;

  &:hover {
    border-color: ${({ theme }) => theme.colors.border};
    color: ${({ theme }) => theme.colors.text};
    background: ${({ theme }) => withAlpha(theme.colors.primary, 0.08)};
  }

  &.primary {
    background: ${({ theme }) => theme.colors.primary};
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.background};
  }

  &.primary:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
    border-color: ${({ theme }) => theme.colors.primaryDark};
  }

  ${props => props.$active && `
    color: ${props.theme.colors.primary};
    background: ${withAlpha(props.theme.colors.primary, 0.12)};
    border-color: ${withAlpha(props.theme.colors.primary, 0.4)};
  `}
`;

const LogoutButton = styled.button`
  font-family: ${({ theme }) => theme.fonts.heading};
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.borderMuted};
  border-radius: 6px;
  color: ${({ theme }) => getMutedText(theme)};
  padding: 8px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;
  font-size: 14px;

  &:hover {
    border-color: ${({ theme }) => theme.colors.border};
    color: ${({ theme }) => theme.colors.text};
    background: ${({ theme }) => withAlpha(theme.colors.primary, 0.08)};
  }
`;

const HudHeader: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { field, logoutField } = useFieldAuth();
  const { referee, logoutReferee } = useRefereeAuth();
  const { items } = useCart();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isRegisterDropdownOpen, setIsRegisterDropdownOpen] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const handleLogout = () => {
    logout();
    closeMobileMenu();
  };

  // Закрытие dropdown при клике вне его
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isRegisterDropdownOpen) {
        setIsRegisterDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isRegisterDropdownOpen]);



  const isClansActive = location.pathname.startsWith('/clans');
  const isLoginActive = location.pathname.includes('/login');

  return (
    <>
      <Header>
        <Container>
          <Logo to="/" onClick={closeMobileMenu}>Nova Warfare</Logo>
          
          {/* Desktop Navigation */}
          <DesktopNav>
            <NavLink to="/" $active={location.pathname === '/'}>
              Home
            </NavLink>
            <NavLink to="/operations" $active={location.pathname === '/operations'}>
              Operations
            </NavLink>
            <NavLink to={`/clans?type=${CLAN_TYPES.AIRSOFT}`} $active={isClansActive}>
              Clans
            </NavLink>
            <NavLink to="/commandos?type=airsoft" $active={location.pathname.startsWith('/commandos')}>
              Commandos
            </NavLink>
            <NavLink to="/fields" $active={location.pathname === '/fields'}>
              Fields
            </NavLink>
            <NavLink to="/intel" $active={location.pathname === '/intel'}>
              Intel
            </NavLink>
            {user && (
              <NavLink to="/profile" $active={location.pathname === '/profile'}>
                Profile
              </NavLink>
            )}
            {field && (
              <NavLink to={`/fields/${field.id}`} $active={location.pathname === `/fields/${field.id}`}>
                My Field
              </NavLink>
            )}
            {referee && (
              <NavLink to="/referee/profile" $active={location.pathname === '/referee/profile'}>
                Profile
              </NavLink>
            )}
          </DesktopNav>

          <DesktopRightSection>
            <ThemeToggle />
            {field && (
              <CartLink to="/cart">
                Cart {items.length > 0 && <CartCount>{items.length}</CartCount>}
              </CartLink>
            )}
            <AuthLinks>
              {user || field || referee ? (
                <>
                  {user && (
                    <LogoutButton onClick={logout}>
                      Player Logout
                    </LogoutButton>
                  )}
                  {field && (
                    <LogoutButton onClick={logoutField}>
                      Field Logout
                    </LogoutButton>
                  )}
                  {referee && (
                    <LogoutButton onClick={() => { logoutReferee(); navigate('/'); }}>
                      Referee Logout
                    </LogoutButton>
                  )}
                </>
              ) : (
                <>
                  <AuthButton 
                    onClick={() => navigate('/login')}
                    $active={isLoginActive}
                  >
                    Login
                  </AuthButton>
                  <ClansDropdown>
                    <ClansDropdownButton 
                      $active={false}
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsRegisterDropdownOpen(!isRegisterDropdownOpen);
                      }}
                    >
                      Register <span style={{fontSize: '10px', transition: 'transform 0.2s ease', transform: isRegisterDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)'}}>▼</span>
                    </ClansDropdownButton>
                    <ClansDropdownContent $isOpen={isRegisterDropdownOpen}>
                      <ClansDropdownItem 
                        to="/register"
                        onClick={() => setIsRegisterDropdownOpen(false)}
                      >
                        Player Registration
                      </ClansDropdownItem>
                      <ClansDropdownItem 
                        to="/register-field"
                        onClick={() => setIsRegisterDropdownOpen(false)}
                      >
                        Field Registration
                      </ClansDropdownItem>
                    </ClansDropdownContent>
                  </ClansDropdown>
                </>
              )}
            </AuthLinks>
          </DesktopRightSection>

          {/* Mobile Menu Button */}
          <MobileMenuButton onClick={toggleMobileMenu}>
            <BurgerLine $isOpen={isMobileMenuOpen} $index={0} />
            <BurgerLine $isOpen={isMobileMenuOpen} $index={1} />
            <BurgerLine $isOpen={isMobileMenuOpen} $index={2} />
          </MobileMenuButton>
        </Container>
      </Header>

      {/* Mobile Menu Overlay */}
      <MobileMenuOverlay $isOpen={isMobileMenuOpen} onClick={closeMobileMenu} />

      {/* Mobile Menu */}
      <MobileMenu $isOpen={isMobileMenuOpen}>
        <MobileNavList>
          <MobileNavLink 
            to="/" 
            $active={location.pathname === '/'} 
            onClick={closeMobileMenu}
          >
            Home
          </MobileNavLink>
          <MobileNavLink 
            to="/operations" 
            $active={location.pathname === '/operations'} 
            onClick={closeMobileMenu}
          >
            Operations
          </MobileNavLink>
          
          <MobileNavLink 
            to={`/clans?type=${CLAN_TYPES.AIRSOFT}`} 
            $active={isClansActive} 
            onClick={closeMobileMenu}
          >
            Clans
          </MobileNavLink>
          
          <MobileNavLink 
            to="/commandos?type=airsoft" 
            $active={location.pathname.startsWith('/commandos')} 
            onClick={closeMobileMenu}
          >
            Commandos
          </MobileNavLink>
          
          <MobileNavLink 
            to="/fields" 
            $active={location.pathname === '/fields'} 
            onClick={closeMobileMenu}
          >
            Fields
          </MobileNavLink>
          <MobileNavLink 
            to="/intel" 
            $active={location.pathname === '/intel'} 
            onClick={closeMobileMenu}
          >
            Intel
          </MobileNavLink>
          {user && (
            <MobileNavLink 
              to="/profile" 
              $active={location.pathname === '/profile'} 
              onClick={closeMobileMenu}
            >
              Profile
            </MobileNavLink>
          )}
          {field && (
            <MobileNavLink
              to={`/fields/${field.id}`}
              $active={location.pathname === `/fields/${field.id}`}
              onClick={closeMobileMenu}
            >
              My Field
            </MobileNavLink>
          )}
          {referee && (
            <MobileNavLink 
              to="/referee/profile" 
              $active={location.pathname === '/referee/profile'} 
              onClick={closeMobileMenu}
            >
              Profile
            </MobileNavLink>
          )}
        </MobileNavList>

        <MobileCartSection>
          <MobileThemeToggleWrapper>
            <ThemeToggle />
          </MobileThemeToggleWrapper>
          {field && (
            <MobileCartLinkWrapper>
              <MobileCartLink to="/cart" onClick={closeMobileMenu}>
                Cart {items.length > 0 && <CartCount>{items.length}</CartCount>}
              </MobileCartLink>
            </MobileCartLinkWrapper>
          )}
        </MobileCartSection>

        <MobileAuthSection>
          {user || field || referee ? (
            <>
              {user && (
                <MobileLogoutButton onClick={handleLogout}>
                  Player Logout
                </MobileLogoutButton>
              )}
              {field && (
                <MobileLogoutButton onClick={() => { logoutField(); closeMobileMenu(); }}>
                  Field Logout
                </MobileLogoutButton>
              )}
              {referee && (
                <MobileLogoutButton onClick={() => { logoutReferee(); navigate('/'); closeMobileMenu(); }}>
                  Referee Logout
                </MobileLogoutButton>
              )}
            </>
          ) : (
            <>
              <MobileAuthButton onClick={() => { navigate('/login'); closeMobileMenu(); }}>
                Login
              </MobileAuthButton>
              
              <MobileClansSection>
                <MobileClansSectionTitle>Registration</MobileClansSectionTitle>
                <MobileClansLink 
                  to="/register" 
                  onClick={closeMobileMenu}
                >
                  Player Registration
                </MobileClansLink>
                <MobileClansLink 
                  to="/register-field" 
                  onClick={closeMobileMenu}
                >
                  Field Registration
                </MobileClansLink>
              </MobileClansSection>
            </>
          )}
        </MobileAuthSection>
      </MobileMenu>
    </>
  );
};

export default HudHeader; 
